import React, { useState, useEffect } from 'react';
import { Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { summaryLines, materialLines } from './exportPdf';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function SendDialog({ open, onOpenChange, cfg, L, makePdf, refCode, user }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  useEffect(() => { if (open && user) setForm((f) => ({ ...f, name: f.name || user.name || '', email: f.email || user.email || '' })); }, [open, user]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let pdf = null;
      try { pdf = (await makePdf()).output('datauristring').split(',')[1]; } catch { pdf = null; }
      const body = {
        ...form, reference: refCode, category: cfg.category, category_label: L.cat.label,
        config: cfg, summary: summaryLines(cfg, L), materials: materialLines(cfg),
      };
      let res = await fetch(`${API_URL}/api/configurations`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...body, pdf_base64: pdf }) });
      if (!res.ok && pdf) res = await fetch(`${API_URL}/api/configurations`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Erreur');
      setDone(true);
      toast.success('Votre configuration a bien été transmise à JENMA Concept');
    } catch (err) {
      toast.error("L'envoi a échoué. Merci de vérifier vos informations.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setDone(false); }}>
      <DialogContent className="bg-[#151515] border-[#a68966]/30 text-gray-200 max-w-lg" data-testid="send-config-dialog">
        <DialogHeader>
          <DialogTitle className="font-playfair text-2xl text-white">{done ? 'Merci, nous avons bien reçu votre projet' : 'Envoyer ma configuration'}</DialogTitle>
          <DialogDescription className="text-gray-400">
            {done ? `Référence ${refCode}. Un architecte d'intérieur JENMA vous recontacte sous 48 h pour affiner votre ${L.cat.label.toLowerCase()}.` : "Nous recevrons vos plans, élévation et perspective en PDF. Un architecte d'intérieur vous recontacte rapidement."}
          </DialogDescription>
        </DialogHeader>
        {done ? (
          <button type="button" onClick={() => onOpenChange(false)} data-testid="send-config-close" className="mt-2 bg-[#a68966] hover:bg-[#b89c78] text-[#1a1a1a] font-semibold py-3 transition-colors">Continuer à configurer</button>
        ) : (
          <form onSubmit={submit} className="space-y-3 mt-2">
            <Input required placeholder="Nom complet *" value={form.name} onChange={set('name')} data-testid="send-config-name" className="bg-[#0f0f0f] border-white/10 rounded-none" />
            <Input required type="email" placeholder="Email *" value={form.email} onChange={set('email')} data-testid="send-config-email" className="bg-[#0f0f0f] border-white/10 rounded-none" />
            <Input placeholder="Téléphone" value={form.phone} onChange={set('phone')} data-testid="send-config-phone" className="bg-[#0f0f0f] border-white/10 rounded-none" />
            <Textarea placeholder="Votre projet, vos envies, l'adresse du chantier..." rows={4} value={form.message} onChange={set('message')} data-testid="send-config-message" className="bg-[#0f0f0f] border-white/10 rounded-none" />
            <button type="submit" disabled={busy} data-testid="send-config-submit"
              className="w-full flex items-center justify-center gap-2 bg-[#a68966] hover:bg-[#b89c78] disabled:opacity-60 text-[#1a1a1a] font-semibold py-3 transition-colors">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {busy ? 'Préparation des plans...' : 'Envoyer à JENMA Concept'}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
